import React, { ReactNode, useMemo, useState } from "react";

// --- Types ---
export type SortDirection = "asc" | "desc" | null;

export interface SortConfig {
  key: string;
  direction: SortDirection;
}

export interface Column<T> {
  key: keyof T | string;
  header: ReactNode;
  render?: (row: T) => ReactNode;
  sortable?: boolean;
}

interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  keyField?: keyof T;
  onRowClick?: (row: T) => void;
  sortConfig?: SortConfig | null;
  onSort?: (key: string) => void;
}

interface PanelProps {
  title?: string;
  children: ReactNode;
  right?: ReactNode;
}

// --- Components ---

export function Panel({ title, children, right }: PanelProps) {
  const showHeader = Boolean(title) || Boolean(right);
  return (
    <section className="panel">
      {showHeader && (
        <div className="panel-header">
          {title && <h2 className="panel-title">{title}</h2>}
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

export function Table<T extends Record<string, any>>({
  columns,
  rows,
  keyField = "id",
  onRowClick,
  sortConfig,
  onSort,
}: TableProps<T>) {
  const getColumnClass = (key: keyof T | string) =>
    `col-${String(key).replace(/[^a-zA-Z0-9_-]/g, "-")}`;

  const handleHeaderClick = (column: Column<T>) => {
    if (column.sortable && onSort) {
      onSort(String(column.key));
    }
  };

  return (
    <div className="table-container">
      <table className="table w-full">
        <thead className="text-left bg-slate-50 text-slate-600">
          <tr>
            {columns.map((c) => {
              const isSorted = sortConfig?.key === c.key;
              const isSortable = Boolean(c.sortable && onSort);

              return (
                <th
                  key={String(c.key)}
                  className={`${getColumnClass(c.key)} px-3 py-2 font-medium text-left ${
                    isSortable
                      ? "cursor-pointer hover:bg-slate-100 select-none"
                      : ""
                  }`}
                  onClick={() => handleHeaderClick(c)}
                >
                  <div className="th-content">
                    {c.header}

                    {isSorted && (
                      <span className="sort-icon active">
                        {sortConfig?.direction === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                    {!isSorted && isSortable && (
                      <span className="sort-icon inactive">↕</span>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r) => (
            <tr
              key={String(r[keyField as string] || JSON.stringify(r))}
              onClick={() => onRowClick && onRowClick(r)}
              className={
                onRowClick
                  ? "cursor-pointer hover:bg-slate-50 transition-colors"
                  : ""
              }
            >
              {columns.map((c) => (
                <td
                  key={String(c.key)}
                  className={`${getColumnClass(c.key)} px-3 py-2 align-top text-left`}
                >
                  {c.render ? c.render(r) : (r[c.key as keyof T] as ReactNode)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function sortData<T>(
  data: T[],
  sortConfig: SortConfig | null,
  // Optional: rules to extract values for specific complex columns
  customExtractors: Record<string, (item: T) => any> = {}
): T[] {
  if (!data || !sortConfig) return data || [];

  return [...data].sort((a, b) => {
    const { key, direction } = sortConfig;

    // get values (use custom extractor if provided, else default access)
    let aValue = customExtractors[key]
      ? customExtractors[key](a)
      : a[key as keyof T];
    let bValue = customExtractors[key]
      ? customExtractors[key](b)
      : b[key as keyof T];

    // handle null/undefined
    if (aValue === null || aValue === undefined) aValue = "";
    if (bValue === null || bValue === undefined) bValue = "";

    let comparison = 0;

    if (typeof aValue === "string" && typeof bValue === "string") {
      // String comparison (case-insensitive, handles accents)
      comparison = aValue.localeCompare(bValue);
    } else {
      if (aValue < bValue) comparison = -1;
      if (aValue > bValue) comparison = 1;
    }

    return direction === "asc" ? comparison : -comparison;
  });
}

/**
 * Client side sorting for tables whose rows are all loaded at once.
 * Clicking a column sorts it ascending; clicking it again flips to
 * descending. `extractors` give the value to sort by for columns whose
 * row field isn't directly comparable (e.g. a count of nested items).
 */
export function useClientSort<T>(
  rows: T[],
  extractors: Record<string, (item: T) => any> = {}
) {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  const onSort = (key: string) => {
    const direction: SortDirection =
      sortConfig?.key === key && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ key, direction });
  };

  const sorted = useMemo(
    () => sortData(rows, sortConfig, extractors),
    // extractors are inline objects, so they are left out of the deps on
    // purpose; they only change when the component's code does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, sortConfig]
  );

  return { sorted, sortConfig, onSort };
}

/**
 * A clickable <th> for hand written tables, matching the sort arrows the
 * shared <Table> component shows.
 */
export function SortableHeader({
  label,
  sortKey,
  sortConfig,
  onSort,
  className = "px-3 py-2 font-medium",
}: {
  label: ReactNode;
  sortKey: string;
  sortConfig: SortConfig | null;
  onSort: (key: string) => void;
  className?: string;
}) {
  const isSorted = sortConfig?.key === sortKey;
  return (
    <th
      className={`${className} cursor-pointer hover:bg-slate-100 select-none`}
      onClick={() => onSort(sortKey)}
    >
      <div className="th-content">
        {label}
        {isSorted ? (
          <span className="sort-icon active">
            {sortConfig?.direction === "asc" ? "▲" : "▼"}
          </span>
        ) : (
          <span className="sort-icon inactive">↕</span>
        )}
      </div>
    </th>
  );
}

export const Empty = ({ children }: { children: ReactNode }) => (
  <div className="empty">{children}</div>
);

export const Badge = ({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) => <span className={`badge ${className}`}>{children}</span>;

export const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <div className="detail-section">
    <h3 className="detail-section-title">{title}</h3>
    <div>{children}</div>
  </div>
);

export const DetailRow = ({
  label,
  value,
  isBool = false,
  isArray = false,
}: {
  label: string;
  value: any;
  isBool?: boolean;
  isArray?: boolean;
}) => {
  let content;

  if (isArray && Array.isArray(value)) {
    content =
      value.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {value.map((item, index) => (
            <span key={index} className="detail-value-array-item">
              {typeof item === "object" && item !== null && "name" in item
                ? item.name
                : String(item)}
            </span>
          ))}
        </div>
      ) : (
        <span style={{ color: "#d1d5db" }}>—</span>
      );
  } else if (isBool) {
    content = value ? (
      <span className="bool-yes">SI</span>
    ) : (
      <span className="bool-no">NO</span>
    );
  } else {
    const displayValue =
      value !== null && value !== undefined && value !== ""
        ? String(value)
        : "—";

    content = (
      <span
        style={{ color: displayValue === "—" ? "#d1d5db" : "#374151" }}
        title={displayValue}
      >
        {displayValue}
      </span>
    );
  }

  return (
    <div className="detail-row">
      <span className="detail-label">{label}:</span>
      <div className="detail-value">{content}</div>
    </div>
  );
};
